import { Injectable, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, NavigationBehaviorOptions, Params, Router } from '@angular/router';
import { Observable, Subject, Subscription, concatMap, filter, firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class QueryParamsService implements OnDestroy {
    private static readonly queryParamsChangedInfo = 'QueryParamsService';

    readonly queryParamsChanged$: Observable<Params>;

    private queryParamChangesSub = new Subject<{ queryParams: Params; navigationBehavior?: NavigationBehaviorOptions }>();

    private readonly subscription = new Subscription();

    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);

    constructor() {
        // using a subscription so two calls to updateQueryParams at the same time are handled sequentially
        const queryParamChangesSubscription = this.queryParamChangesSub
            .pipe(concatMap(({ queryParams, navigationBehavior }) => this.applyQueryParams(queryParams, navigationBehavior)))
            .subscribe();

        this.subscription.add(queryParamChangesSubscription);

        this.queryParamsChanged$ = this.activatedRoute.queryParams.pipe(
            // filter out query param changes that were triggered by this service itself
            filter(() => this.router.currentNavigation()?.extras.info !== QueryParamsService.queryParamsChangedInfo)
        );
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    updateQueryParams(queryParams: Params, navigationBehavior?: NavigationBehaviorOptions): void {
        this.queryParamChangesSub.next({ queryParams, navigationBehavior });
    }

    private async applyQueryParams(queryParams: Params, navigationBehavior?: NavigationBehaviorOptions): Promise<boolean> {
        const currentParams = await firstValueFrom(this.activatedRoute.queryParams);
        return this.router.navigate([], {
            ...navigationBehavior,
            relativeTo: this.activatedRoute,
            queryParams: { ...currentParams, ...queryParams },
            info: QueryParamsService.queryParamsChangedInfo
        });
    }
}
